import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Apple, Stethoscope, Scissors, Pill, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import HeroProgressBar from './HeroProgressBar';

const AUTO_PLAY_DURATION = 5000; // ms per slide

/**
 * HeroServiceCarousel
 * Premium animated service carousel for the Hero section.
 * Uses Framer Motion for slide, fade, and zoom animations.
 * Supports auto-play, touch/swipe, keyboard navigation, and ARIA attributes.
 */
const HeroServiceCarousel = () => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const autoPlayRef = useRef(null);
  const touchStartX = useRef(null);

  // ── Service data read directly from LanguageContext ──────────────────────
  const services = useMemo(
    () => [
      {
        key: 'service1',
        icon: Apple,
        titleKey: 'service1_title',
        descKey: 'service1_desc',
        // Nutritional counselling – vibrant food image from existing ServicesSection
        image:
          'https://images.unsplash.com/photo-1757596057470-19d36962705d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwZm9vZCUyMG51dHJpdGlvbiUyMHNhbGFkJTIwZnJlc2glMjB2ZWdldGFibGVzfGVufDB8fHx8MTc3MTc1NDgzN3ww&ixlib=rb-4.1.0&q=85',
        accent: '#10b981', // emerald
        badge: 'Personalized Plans',
      },
      {
        key: 'service4',
        icon: Pill,
        titleKey: 'service4_title',
        descKey: 'service4_desc',
        // GLP-1 – existing local asset
        image: process.env.PUBLIC_URL + '/img-01.jpg',
        accent: '#f59e0b', // amber
        badge: 'Modern Medicine',
      },
      {
        key: 'service2',
        icon: Stethoscope,
        titleKey: 'service2_title',
        descKey: 'service2_desc',
        // Endobariatric – existing local asset
        image: process.env.PUBLIC_URL + '/img-02.jfif',
        accent: '#3b82f6', // blue
        badge: 'Non-Surgical',
      },
      {
        key: 'service3',
        icon: Scissors,
        titleKey: 'service3_title',
        descKey: 'service3_desc',
        // Bariatric Surgery – existing local asset
        image: process.env.PUBLIC_URL + '/img-03.jpeg',
        accent: '#800000', // maroon brand colour
        badge: 'Advanced Surgery',
      },
    ],
    // We intentionally don't include t() in deps; re-render happens via language change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const total = services.length;

  // ── Navigation helpers ────────────────────────────────────────────────────
  const goTo = useCallback(
    (index, dir) => {
      setDirection(dir ?? (index > currentIndex ? 1 : -1));
      setCurrentIndex((index + total) % total);
    },
    [currentIndex, total]
  );

  const goNext = useCallback(() => goTo(currentIndex + 1, 1), [currentIndex, goTo]);
  const goPrev = useCallback(() => goTo(currentIndex - 1, -1), [currentIndex, goTo]);

  // ── Auto-play ─────────────────────────────────────────────────────────────
  const resetAutoPlay = useCallback(() => {
    clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(goNext, AUTO_PLAY_DURATION);
  }, [goNext]);

  useEffect(() => {
    resetAutoPlay();
    return () => clearInterval(autoPlayRef.current);
  }, [resetAutoPlay]);

  // ── Keyboard navigation ───────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') { goNext(); resetAutoPlay(); }
      if (e.key === 'ArrowLeft') { goPrev(); resetAutoPlay(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, resetAutoPlay]);

  // ── Touch / Swipe support ─────────────────────────────────────────────────
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) {
      if (delta < 0) { goNext(); resetAutoPlay(); }
      else { goPrev(); resetAutoPlay(); }
    }
    touchStartX.current = null;
  };

  // ── Framer Motion variants ────────────────────────────────────────────────
  const slideVariants = {
    enter: (dir) => ({
      x: dir > 0 ? '60%' : '-60%',
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] },
    },
    exit: (dir) => ({
      x: dir > 0 ? '-60%' : '60%',
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.4, ease: [0.55, 0, 1, 0.45] },
    }),
  };

  const textVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1 + 0.2, duration: 0.5, ease: 'easeOut' },
    }),
  };

  const imageVariants = {
    hidden: { scale: 1.08, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] },
    },
    hover: { scale: 1.06, transition: { duration: 4, ease: 'easeOut' } },
  };

  const current = services[currentIndex];
  // Shorten description for the card (max 20 words)
  const shortDesc = (key) => {
    const full = t(key);
    const words = full.split(' ');
    return words.length > 20 ? words.slice(0, 20).join(' ') + '…' : full;
  };

  return (
    <div
      className="relative w-full select-none"
      role="region"
      aria-label="Service showcase carousel"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ── Glassmorphism card ──────────────────────────────────────────── */}
      <div
        className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/40"
        style={{
          background: 'rgba(255,255,255,0.88)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
        }}
      >
        {/* Progress bar */}
        <div className="px-4 pt-4">
          <HeroProgressBar duration={AUTO_PLAY_DURATION} slideKey={currentIndex} />
        </div>

        {/* Slide counter + dot indicators */}
        <div className="flex items-center justify-between px-5 pt-3 pb-1">
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
            {String(currentIndex + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
          </span>

          <div className="flex items-center gap-2" role="tablist" aria-label="Carousel slides">
            {services.map((_, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={i === currentIndex}
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => { goTo(i); resetAutoPlay(); }}
                className="transition-all duration-300 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#800000]"
                style={{
                  width: i === currentIndex ? 24 : 8,
                  height: 8,
                  background: i === currentIndex ? '#800000' : '#cbd5e1',
                }}
              />
            ))}
          </div>
        </div>

        {/* ── Animated slide area ─────────────────────────────────────── */}
        <div className="relative overflow-hidden">
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="flex flex-col"
            >
              {/* Image — aspect-ratio box keeps image fully inside the card */}
              <div className="relative w-full overflow-hidden rounded-t-2xl" style={{ aspectRatio: '16/9' }}>
                <motion.img
                  src={current.image}
                  alt={t(current.titleKey)}
                  loading="lazy"
                  variants={imageVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
                {/* Gradient overlay on image */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                {/* Badge on image */}
                <div
                  className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-[10px] font-bold uppercase tracking-widest"
                  style={{ background: current.accent }}
                >
                  {current.badge}
                </div>

                {/* Icon */}
                <div
                  className="absolute bottom-3 right-3 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
                  style={{ background: current.accent + 'dd' }}
                >
                  <current.icon className="w-5 h-5 text-white" />
                </div>
              </div>

              {/* Text content */}
              <div className="flex flex-col flex-grow px-5 pt-4 pb-5">
                <motion.h3
                  custom={0}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  className="text-xl font-extrabold text-slate-900 leading-tight mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {t(current.titleKey)}
                </motion.h3>

                <motion.p
                  custom={1}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  className="text-slate-600 text-sm leading-relaxed flex-grow"
                >
                  {shortDesc(current.descKey)}
                </motion.p>

                <motion.div
                  custom={2}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  className="mt-3 pt-3 border-t border-slate-100"
                >
                  {/* <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-[#800000] font-bold text-sm uppercase tracking-wider group/link hover:gap-3 transition-all duration-200"
                    aria-label={`Learn more about ${t(current.titleKey)}`}
                  >
                    {t('learn_more')}
                    <ArrowRight
                      className="w-4 h-4 group-hover/link:translate-x-1 transition-transform duration-200"
                    />
                  </Link> */}
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* ── Prev / Next buttons (outside card, below) ───────────────────── */}
      <div className="flex justify-center gap-3 mt-4">
        <button
          onClick={() => { goPrev(); resetAutoPlay(); }}
          aria-label="Previous service"
          className="w-11 h-11 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center hover:border-[#800000] hover:text-[#800000] text-slate-500 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#800000]"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => { goNext(); resetAutoPlay(); }}
          aria-label="Next service"
          className="w-11 h-11 rounded-full bg-[#800000] border border-[#800000] shadow-md flex items-center justify-center hover:bg-black text-white transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#800000]"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default HeroServiceCarousel;
